import { Tooltip } from "primereact/tooltip";
import "./dashboardGraphs.css";

export interface HeatCell {
  row: string;
  col: string;
  value: number;
  // Extra text shown in the cell tooltip
  detail?: string;
}

interface HeatMatrixProps {
  cells: HeatCell[];
  rowTitle: string;
  colTitle: string;
  formatValue?: (v: number) => string;
  color?: string; // rgb triplet, e.g. "47, 126, 216"
}

/**
 * Cross-tab table with cell intensity proportional to its value.
 */
export default function HeatMatrix({
  cells,
  rowTitle,
  colTitle,
  formatValue = (v) => v.toLocaleString("es-CL"),
  color = "47, 126, 216",
}: HeatMatrixProps) {
  const rows = Array.from(new Set(cells.map((c) => c.row)));
  const cols = Array.from(new Set(cells.map((c) => c.col)));
  const max = Math.max(1, ...cells.map((c) => Math.abs(c.value)));
  const byKey = new Map(cells.map((c) => [`${c.row}|${c.col}`, c]));

  const rowTotal = (r: string) =>
    cells.filter((c) => c.row === r).reduce((a, c) => a + c.value, 0);
  const colTotal = (col: string) =>
    cells.filter((c) => c.col === col).reduce((a, c) => a + c.value, 0);
  const total = cells.reduce((a, c) => a + c.value, 0);

  return (
    <div className="dg-heat-wrapper">
      <Tooltip target=".dg-heat-cell[data-pr-tooltip]" position="top" />
      <table className="dg-heat">
        <thead>
          <tr>
            <th className="dg-heat-corner">
              <span>{rowTitle}</span>
              <span>↓ / {colTitle} →</span>
            </th>
            {cols.map((c) => (
              <th key={c}>{c}</th>
            ))}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r}>
              <th>{r}</th>
              {cols.map((c) => {
                const cell = byKey.get(`${r}|${c}`);
                const intensity = cell ? Math.abs(cell.value) / max : 0;
                return (
                  <td
                    key={c}
                    className="dg-heat-cell"
                    data-pr-tooltip={cell?.detail}
                    style={{
                      backgroundColor: `rgba(${color}, ${0.08 + intensity * 0.72})`,
                      color: intensity > 0.55 ? "#ffffff" : "#1f2937",
                    }}
                  >
                    {cell ? formatValue(cell.value) : "–"}
                  </td>
                );
              })}
              <td className="dg-heat-total">{formatValue(rowTotal(r))}</td>
            </tr>
          ))}
          <tr>
            <th>Total</th>
            {cols.map((c) => (
              <td key={c} className="dg-heat-total">
                {formatValue(colTotal(c))}
              </td>
            ))}
            <td className="dg-heat-total">{formatValue(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
